import { GRAVITY, JUMP_FORCE, MOVE_SPEED, GROUND_HEIGHT, WEAPON_TYPES, DEPTH_MOVE_SPEED, ROAD_TOP_MARGIN, WALKABLE_DEPTH, DEFAULT_GROUND_Y, getDrawY, getDepthScale } from './constants';
import soundManager from '../utils/SoundManager';

export class Player {
  constructor(x, y, color, spriteUrl = null, nftId = null) {
    this.x = x;
    // 2.5D depth: groundY = position on the road depth axis (0=back, WALKABLE_DEPTH=front)
    this.groundY = DEFAULT_GROUND_Y;
    // Jump altitude (0 = on ground, positive = in air)
    this.jumpZ = 0;
    this.jumpVelocity = 0;
    // y will be computed from groundY and jumpZ in update()
    this.y = y;
    // Larger size when using sprite image or special NFT character
    const isSpecialNft = nftId && nftId.startsWith('nft_');
    this.width = (spriteUrl || isSpecialNft) ? 50 : 40;
    this.height = (spriteUrl || isSpecialNft) ? 75 : 60;
    this.velocityX = 0;
    this.isGrounded = true;
    this.color = color;
    this.health = 100;
    this.maxHealth = 100;
    this.direction = 1;
    this.isJumping = false;
    this.animFrame = 0;
    this.nftId = nftId;
    this.grenadeThrowAnim = 0;
    // Power-up states
    this.hasShield = false;
    this.shieldTimer = 0;
    this.rapidFire = false;
    this.rapidFireTimer = 0;
    this.speedBoost = false;
    this.speedTimer = 0;
    this.grenades = 0;
    // New power-up states
    this.spreadShot = false;
    this.spreadShotTimer = 0;
    this.doubleDamage = false;
    this.doubleDamageTimer = 0;
    this.freezeActive = false;
    this.freezeTimer = 0;
    this.magnetActive = false;
    this.magnetTimer = 0;
    this.laserActive = false;
    this.laserTimer = 0;
    this.explosiveRounds = false;
    this.explosiveRoundsTimer = 0;
    // Weapon states
    this.currentWeapon = WEAPON_TYPES.DEFAULT;
    this.rocketAmmo = 0;
    this.tankAmmo = 0;
    this.rifleAmmo = 0;
    this.inTank = false;
    this.tankArmor = 0;
    // Sprite image
    this.spriteUrl = spriteUrl;
    this.spriteImage = null;
    this.spriteLoaded = false;
    if (spriteUrl) {
      this.loadSprite(spriteUrl);
    }
  }

  loadSprite(url) {
    this.spriteImage = new Image();
    this.spriteImage.crossOrigin = 'anonymous';
    this.spriteImage.onload = () => {
      this.spriteLoaded = true;
    };
    this.spriteImage.onerror = () => {
      console.warn('Failed to load player sprite, using fallback');
      this.spriteLoaded = false;
    };
    this.spriteImage.src = url;
  }

  update(canvasHeight, keys) {
    const speed = this.speedBoost ? MOVE_SPEED * 1.5 : MOVE_SPEED;
    
    // Horizontal movement
    if (keys.left) {
      this.velocityX = -speed;
      this.direction = -1;
    } else if (keys.right) {
      this.velocityX = speed;
      this.direction = 1;
    } else {
      this.velocityX = 0;
    }

    // Depth movement (up/down on the road)
    const depthSpeed = this.speedBoost ? DEPTH_MOVE_SPEED * 1.5 : DEPTH_MOVE_SPEED;
    if (keys.up) {
      this.groundY -= depthSpeed;
    }
    if (keys.down) {
      this.groundY += depthSpeed;
    }
    this.groundY = Math.max(0, Math.min(WALKABLE_DEPTH, this.groundY));

    // Jump (separate from depth movement)
    if (keys.jump && this.isGrounded) {
      this.jumpVelocity = JUMP_FORCE; // Positive = upward
      this.isGrounded = false;
      this.isJumping = true;
      soundManager.playJump();
    }

    // Jump physics
    this.jumpVelocity -= GRAVITY;
    this.jumpZ += this.jumpVelocity;
    if (this.jumpZ <= 0) {
      this.jumpZ = 0;
      this.jumpVelocity = 0;
      this.isGrounded = true;
      this.isJumping = false;
    }

    // Horizontal position
    this.x += this.velocityX;
    if (this.x < 0) this.x = 0;

    // Compute visual Y from groundY and jumpZ
    this.y = getDrawY(canvasHeight, this.groundY, this.height, this.jumpZ);

    // Animation
    if (this.velocityX !== 0 || keys.up || keys.down) {
      this.animFrame = (this.animFrame + 0.2) % 4;
    }

    // Update power-up timers
    if (this.shieldTimer > 0) {
      this.shieldTimer--;
      if (this.shieldTimer <= 0) this.hasShield = false;
    }
    if (this.rapidFireTimer > 0) {
      this.rapidFireTimer--;
      if (this.rapidFireTimer <= 0) this.rapidFire = false;
    }
    if (this.speedTimer > 0) {
      this.speedTimer--;
      if (this.speedTimer <= 0) this.speedBoost = false;
    }
    if (this.spreadShotTimer > 0) {
      this.spreadShotTimer--;
      if (this.spreadShotTimer <= 0) this.spreadShot = false;
    }
    if (this.doubleDamageTimer > 0) {
      this.doubleDamageTimer--;
      if (this.doubleDamageTimer <= 0) this.doubleDamage = false;
    }
    if (this.freezeTimer > 0) {
      this.freezeTimer--;
      if (this.freezeTimer <= 0) this.freezeActive = false;
    }
    if (this.magnetTimer > 0) {
      this.magnetTimer--;
      if (this.magnetTimer <= 0) this.magnetActive = false;
    }
    if (this.laserTimer > 0) {
      this.laserTimer--;
      if (this.laserTimer <= 0) this.laserActive = false;
    }
    if (this.explosiveRoundsTimer > 0) {
      this.explosiveRoundsTimer--;
      if (this.explosiveRoundsTimer <= 0) this.explosiveRounds = false;
    }
    
    // Update grenade throw animation
    if (this.grenadeThrowAnim > 0) {
      this.grenadeThrowAnim--;
    }
  }
  
  startGrenadeThrow() {
    this.grenadeThrowAnim = 30;
  }

  takeDamage(amount) {
    if (this.hasShield) {
      return false;
    }
    if (this.inTank && this.tankArmor > 0) {
      this.tankArmor -= amount;
      if (this.tankArmor <= 0) {
        this.inTank = false;
        this.tankArmor = 0;
        this.currentWeapon = WEAPON_TYPES.DEFAULT;
      }
      soundManager.playHit();
      return false;
    }
    this.health -= amount;
    soundManager.playHit();
    return true;
  }

  switchWeapon() {
    // Cycle through owned weapons: DEFAULT -> ASSAULT_RIFLE -> ROCKET_LAUNCHER -> MINI_TANK -> DEFAULT
    // Skip any weapon whose ammo is depleted.
    const order = [WEAPON_TYPES.DEFAULT, WEAPON_TYPES.ASSAULT_RIFLE, WEAPON_TYPES.ROCKET_LAUNCHER, WEAPON_TYPES.MINI_TANK];
    const currentIdx = order.indexOf(this.currentWeapon);

    // Exit tank if leaving MINI_TANK
    const exitTank = () => { this.inTank = false; this.tankArmor = 0; };

    for (let step = 1; step <= order.length; step++) {
      const next = order[(currentIdx + step) % order.length];
      if (next === WEAPON_TYPES.DEFAULT) {
        if (this.currentWeapon === WEAPON_TYPES.MINI_TANK) exitTank();
        this.currentWeapon = WEAPON_TYPES.DEFAULT;
        return 'Default';
      }
      if (next === WEAPON_TYPES.ASSAULT_RIFLE && this.rifleAmmo > 0) {
        if (this.currentWeapon === WEAPON_TYPES.MINI_TANK) exitTank();
        this.currentWeapon = WEAPON_TYPES.ASSAULT_RIFLE;
        return 'Assault Rifle';
      }
      if (next === WEAPON_TYPES.ROCKET_LAUNCHER && this.rocketAmmo > 0) {
        if (this.currentWeapon === WEAPON_TYPES.MINI_TANK) exitTank();
        this.currentWeapon = WEAPON_TYPES.ROCKET_LAUNCHER;
        return 'Rocket Launcher';
      }
      if (next === WEAPON_TYPES.MINI_TANK && this.tankAmmo > 0) {
        this.currentWeapon = WEAPON_TYPES.MINI_TANK;
        this.inTank = true;
        this.tankArmor = 50;
        return 'Mini Tank';
      }
    }
    return null;
  }

  draw(ctx, cameraX) {
    const screenX = this.x - cameraX;
    const baseColor = this.color;
    const darkerColor = this.darkenColor(baseColor, 30);
    
    ctx.save();

    // Apply depth-based perspective scaling
    const scale = getDepthScale(this.groundY);
    const cx = screenX + this.width / 2;
    const cy = this.y + this.height / 2;
    ctx.translate(cx, cy);
    ctx.scale(scale, scale);
    ctx.translate(-cx, -cy);

    // Draw shield effect
    if (this.hasShield) {
      ctx.beginPath();
      ctx.arc(screenX + 20, this.y + 30, 45, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(20, 241, 149, ${0.5 + Math.sin(Date.now() / 100) * 0.3})`;
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = 'rgba(20, 241, 149, 0.1)';
      ctx.fill();
    }

    // Speed boost trail
    if (this.speedBoost) {
      ctx.globalAlpha = 0.3;
      for (let i = 1; i <= 3; i++) {
        if (this.spriteLoaded && this.spriteImage) {
          const imgAspect = this.spriteImage.width / this.spriteImage.height;
          const targetAspect = this.width / this.height;
          let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
          if (imgAspect > targetAspect) {
            drawWidth = this.width;
            drawHeight = this.width / imgAspect;
            offsetY = (this.height - drawHeight) / 2;
          } else {
            drawHeight = this.height;
            drawWidth = this.height * imgAspect;
            offsetX = (this.width - drawWidth) / 2;
          }
          ctx.drawImage(this.spriteImage, screenX - i * 15 * this.direction + offsetX, this.y + offsetY, drawWidth, drawHeight);
        } else {
          ctx.fillStyle = baseColor;
          ctx.fillRect(screenX - i * 10 * this.direction, this.y + 25, 30, 25);
        }
      }
      ctx.globalAlpha = 1;
    }

    // Spread shot effect - magenta aura
    if (this.spreadShot) {
      ctx.beginPath();
      ctx.arc(screenX + 20, this.y + 30, 35, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 0, 255, ${0.4 + Math.sin(Date.now() / 80) * 0.2})`;
      ctx.lineWidth = 2;
      ctx.stroke();
      for (let angle of [-0.3, 0, 0.3]) {
        ctx.beginPath();
        ctx.moveTo(screenX + 20, this.y + 30);
        ctx.lineTo(screenX + 20 + Math.cos(angle) * 50 * this.direction, this.y + 30 + Math.sin(angle) * 30);
        ctx.strokeStyle = 'rgba(255, 0, 255, 0.5)';
        ctx.stroke();
      }
    }

    // Double damage effect - pulsing pink glow
    if (this.doubleDamage) {
      const pulse = Math.sin(Date.now() / 100) * 0.3 + 0.5;
      ctx.shadowColor = '#FF1493';
      ctx.shadowBlur = 20 * pulse;
      ctx.beginPath();
      ctx.arc(screenX + 20, this.y + 30, 30, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 20, 147, ${0.1 * pulse})`;
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.font = 'bold 10px "Press Start 2P", monospace';
      ctx.fillStyle = '#FF1493';
      ctx.fillText('2x', screenX + 15, this.y - 20);
    }

    // Magnet effect - purple particles circling
    if (this.magnetActive) {
      const time = Date.now() / 500;
      for (let i = 0; i < 6; i++) {
        const angle = time + (i * Math.PI / 3);
        const radius = 40 + Math.sin(time * 2 + i) * 5;
        const px = screenX + 20 + Math.cos(angle) * radius;
        const py = this.y + 30 + Math.sin(angle) * radius * 0.6;
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(148, 0, 211, ${0.7 + Math.sin(time + i) * 0.3})`;
        ctx.fill();
      }
    }

    // Laser effect - green glow around weapon
    if (this.laserActive) {
      ctx.shadowColor = '#00FF00';
      ctx.shadowBlur = 15;
      ctx.strokeStyle = '#00FF00';
      ctx.lineWidth = 2;
      ctx.beginPath();
      const gunX = this.direction === 1 ? screenX + 35 : screenX - 10;
      ctx.moveTo(gunX, this.y + 35);
      ctx.lineTo(gunX + 30 * this.direction, this.y + 35);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    // Explosive rounds effect - fiery aura
    if (this.explosiveRounds) {
      const flicker = Math.random() * 0.2;
      ctx.beginPath();
      ctx.arc(screenX + 20, this.y + 30, 38, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 69, 0, ${0.15 + flicker})`;
      ctx.fill();
      for (let i = 0; i < 4; i++) {
        const fx = screenX + 10 + Math.random() * 20;
        const fy = this.y + 50 + Math.random() * 10;
        ctx.beginPath();
        ctx.arc(fx, fy, 3 + Math.random() * 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, ${100 + Math.random() * 100}, 0, 0.7)`;
        ctx.fill();
      }
    }

    // Freeze active indicator
    if (this.freezeActive) {
      ctx.fillStyle = '#00FFFF';
      ctx.font = 'bold 8px "Press Start 2P", monospace';
      ctx.fillText('FREEZE', screenX + 5, this.y - 25);
      for (let i = 0; i < 5; i++) {
        const time = Date.now() / 1000;
        const ix = screenX + 10 + i * 8 + Math.sin(time + i) * 5;
        const iy = this.y - 5 - Math.abs(Math.sin(time * 2 + i)) * 15;
        ctx.fillStyle = 'rgba(0, 255, 255, 0.7)';
        ctx.beginPath();
        ctx.moveTo(ix, iy - 4);
        ctx.lineTo(ix + 3, iy);
        ctx.lineTo(ix, iy + 4);
        ctx.lineTo(ix - 3, iy);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Tank mode / NFT rendering / Default rendering
    if (this.inTank) {
      this.drawTankMode(ctx, screenX);
    } else if (this.nftId === 'nft_foot_stabber') {
      this.drawFootStabber(ctx, screenX);
    } else if (this.nftId === 'nft_ofts') {
      this.drawOFTS(ctx, screenX);
    } else if (this.nftId === 'nft_cyber_phantom') {
      this.drawCyberPhantom(ctx, screenX);
    } else if (this.nftId === 'nft_molten_guardian') {
      this.drawMoltenGuardian(ctx, screenX);
    } else if (this.nftId === 'nft_arctic_sentinel') {
      this.drawArcticSentinel(ctx, screenX);
    } else if (this.nftId === 'nft_shadow_assassin') {
      this.drawShadowAssassin(ctx, screenX);
    } else if (this.nftId === 'nft_nature_spirit') {
      this.drawNatureSpirit(ctx, screenX);
    } else if (this.nftId === 'nft_void_walker') {
      this.drawVoidWalker(ctx, screenX);
    } else if (this.nftId === 'nft_solar_knight') {
      this.drawSolarKnight(ctx, screenX);
    } else if (this.nftId === 'nft_thunder_lord') {
      this.drawThunderLord(ctx, screenX);
    } else if (this.spriteLoaded && this.spriteImage) {
      ctx.save();
      const imgAspect = this.spriteImage.width / this.spriteImage.height;
      const targetAspect = this.width / this.height;
      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
      if (imgAspect > targetAspect) {
        drawWidth = this.width;
        drawHeight = this.width / imgAspect;
        offsetY = (this.height - drawHeight) / 2;
      } else {
        drawHeight = this.height;
        drawWidth = this.height * imgAspect;
        offsetX = (this.width - drawWidth) / 2;
      }
      if (this.direction === -1) {
        ctx.scale(-1, 1);
        ctx.drawImage(this.spriteImage, -screenX - this.width + offsetX, this.y + offsetY, drawWidth, drawHeight);
      } else {
        ctx.drawImage(this.spriteImage, screenX + offsetX, this.y + offsetY, drawWidth, drawHeight);
      }
      ctx.restore();
      if (this.currentWeapon === WEAPON_TYPES.ROCKET_LAUNCHER) {
        ctx.fillStyle = '#FF4444';
        const weaponX = this.direction === 1 ? screenX + this.width - 5 : screenX - 20;
        ctx.fillRect(weaponX, this.y + 25, 25, 8);
        ctx.fillStyle = '#333';
        ctx.fillRect(this.direction === 1 ? weaponX + 20 : weaponX, this.y + 26, 6, 6);
      }
    } else {
      // Fallback: Normal player rendering (procedural Lego character)
      const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
      ctx.fillStyle = darkerColor;
      ctx.fillRect(screenX + 8, this.y + 45 + legOffset, 10, 18);
      ctx.fillRect(screenX + 22, this.y + 45 - legOffset, 10, 18);
      ctx.fillStyle = baseColor;
      ctx.fillRect(screenX + 5, this.y + 25, 30, 25);
      const armSwing = Math.sin(this.animFrame * Math.PI) * 15;
      ctx.fillStyle = darkerColor;
      ctx.save();
      ctx.translate(screenX + 5, this.y + 28);
      ctx.rotate((this.direction === 1 ? -armSwing : armSwing) * Math.PI / 180);
      ctx.fillRect(-4, 0, 8, 20);
      ctx.restore();
      ctx.save();
      ctx.translate(screenX + 35, this.y + 28);
      ctx.rotate((this.direction === 1 ? armSwing : -armSwing) * Math.PI / 180);
      ctx.fillRect(-4, 0, 8, 20);
      if (this.currentWeapon === WEAPON_TYPES.ROCKET_LAUNCHER) {
        ctx.fillStyle = '#FF4444';
        ctx.fillRect(-2, 10, 25 * this.direction, 10);
        ctx.fillStyle = '#333';
        ctx.fillRect(this.direction === 1 ? 18 : -23, 12, 8, 6);
      } else {
        ctx.fillStyle = this.rapidFire ? '#14F195' : '#333';
        ctx.fillRect(-2, 15, 15 * this.direction, 6);
      }
      ctx.restore();
      ctx.fillStyle = '#FFCC99';
      ctx.fillRect(screenX + 8, this.y, 24, 24);
      ctx.fillStyle = '#000';
      const eyeX = this.direction === 1 ? 4 : -2;
      ctx.fillRect(screenX + 12 + eyeX, this.y + 8, 4, 4);
      ctx.fillRect(screenX + 22 + eyeX, this.y + 8, 4, 4);
      ctx.fillRect(screenX + 14, this.y + 16, 12, 3);
    }
    
    // Health bar
    ctx.fillStyle = '#333';
    ctx.fillRect(screenX, this.y - 15, 40, 6);
    ctx.fillStyle = this.health > 30 ? '#14F195' : '#FF3B30';
    ctx.fillRect(screenX, this.y - 15, (this.health / this.maxHealth) * 40, 6);

    // Tank armor bar
    if (this.inTank && this.tankArmor > 0) {
      ctx.fillStyle = '#333';
      ctx.fillRect(screenX, this.y - 22, 40, 4);
      ctx.fillStyle = '#4A90D9';
      ctx.fillRect(screenX, this.y - 22, (this.tankArmor / 50) * 40, 4);
    }
    
    // Draw shadow on the ground (depth indicator)
    const shadowAlpha = 0.3 - (this.jumpZ / 300);
    if (shadowAlpha > 0) {
      ctx.globalAlpha = shadowAlpha;
      ctx.fillStyle = '#000';
      ctx.beginPath();
      const shadowScale = 1 - this.jumpZ / 400;
      ctx.ellipse(screenX + this.width / 2, this.y + this.height + this.jumpZ, 
                   this.width * 0.4 * shadowScale, 6 * shadowScale, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    
    ctx.restore();
  }

  drawFootStabber(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 15;
    const greyBody = '#808080';
    const darkGrey = '#5a5a5a';
    const black = '#1a1a1a';
    const skinTone = '#FFCC99';
    const white = '#FFFFFF';
    const gold = '#FFD700';
    
    ctx.save();
    if (this.direction === -1) {
      ctx.scale(-1, 1);
      screenX = -screenX - this.width;
    }
    
    ctx.fillStyle = darkGrey;
    ctx.fillRect(screenX + 12, this.y + 50 + legOffset, 10, 22);
    ctx.fillRect(screenX + 28, this.y + 50 - legOffset, 10, 22);
    ctx.fillStyle = black;
    ctx.fillRect(screenX + 10, this.y + 68 + legOffset, 14, 6);
    ctx.fillRect(screenX + 26, this.y + 68 - legOffset, 14, 6);
    ctx.fillStyle = greyBody;
    ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    ctx.fillStyle = black;
    ctx.beginPath();
    ctx.moveTo(screenX + 10, this.y + 28);
    ctx.lineTo(screenX + 22, this.y + 28);
    ctx.lineTo(screenX + 18, this.y + 45);
    ctx.lineTo(screenX + 10, this.y + 54);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(screenX + 28, this.y + 28);
    ctx.lineTo(screenX + 40, this.y + 28);
    ctx.lineTo(screenX + 40, this.y + 54);
    ctx.lineTo(screenX + 32, this.y + 45);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = white;
    ctx.beginPath();
    ctx.moveTo(screenX + 22, this.y + 28);
    ctx.lineTo(screenX + 28, this.y + 28);
    ctx.lineTo(screenX + 25, this.y + 40);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = black;
    ctx.beginPath();
    ctx.moveTo(screenX + 22, this.y + 30);
    ctx.lineTo(screenX + 25, this.y + 33);
    ctx.lineTo(screenX + 28, this.y + 30);
    ctx.lineTo(screenX + 25, this.y + 27);
    ctx.closePath();
    ctx.fill();
    
    ctx.fillStyle = black;
    ctx.save();
    ctx.translate(screenX + 8, this.y + 30);
    ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 22);
    ctx.fillStyle = white;
    ctx.fillRect(-3, 18, 6, 6);
    ctx.restore();
    
    ctx.save();
    ctx.translate(screenX + 42, this.y + 30);
    const isThrowing = this.grenadeThrowAnim > 0;
    const throwAngle = isThrowing ? -45 + (this.grenadeThrowAnim / 30) * 60 : armSwing;
    ctx.rotate(throwAngle * Math.PI / 180);
    ctx.fillStyle = black;
    ctx.fillRect(-4, 0, 8, 22);
    ctx.fillStyle = white;
    ctx.fillRect(-3, 18, 6, 6);
    if (isThrowing) {
      ctx.fillStyle = '#4A5C3D';
      ctx.beginPath();
      ctx.arc(0, 22, 4, 0, Math.PI * 2);
      ctx.fill();
    } else {
      if (this.currentWeapon === 'rocket_launcher') {
        ctx.fillStyle = '#444';
        ctx.fillRect(-2, 16, 25, 8);
        ctx.fillRect(18, 10, 8, 14);
        ctx.fillStyle = '#FF4444';
        ctx.fillRect(22, 12, 4, 4);
      } else if (this.currentWeapon === 'mini_tank') {
        ctx.fillStyle = '#555';
        ctx.fillRect(0, 14, 12, 10);
      } else {
        ctx.fillStyle = '#333';
        ctx.fillRect(2, 16, 15, 5);
        ctx.fillRect(12, 12, 3, 8);
        ctx.fillStyle = '#222';
        ctx.fillRect(14, 17, 5, 3);
      }
    }
    ctx.restore();
    
    ctx.fillStyle = skinTone;
    ctx.fillRect(screenX + 12, this.y + 8, 26, 22);
    ctx.strokeStyle = gold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(screenX + 32, this.y + 16, 5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(screenX + 37, this.y + 16);
    ctx.quadraticCurveTo(screenX + 42, this.y + 22, screenX + 38, this.y + 28);
    ctx.strokeStyle = gold;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = black;
    ctx.fillRect(screenX + 18, this.y + 14, 4, 5);
    ctx.fillRect(screenX + 30, this.y + 14, 4, 5);
    ctx.fillStyle = black;
    ctx.save();
    ctx.translate(screenX + 20, this.y + 12);
    ctx.rotate(-0.2);
    ctx.fillRect(-3, 0, 8, 2);
    ctx.restore();
    ctx.save();
    ctx.translate(screenX + 32, this.y + 12);
    ctx.rotate(0.2);
    ctx.fillRect(-3, 0, 8, 2);
    ctx.restore();
    ctx.strokeStyle = black;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(screenX + 20, this.y + 24);
    ctx.quadraticCurveTo(screenX + 25, this.y + 27, screenX + 32, this.y + 23);
    ctx.stroke();
    ctx.fillStyle = black;
    ctx.fillRect(screenX + 8, this.y + 6, 34, 4);
    ctx.fillRect(screenX + 14, this.y - 18, 22, 26);
    ctx.fillStyle = '#8B0000';
    ctx.fillRect(screenX + 14, this.y + 2, 22, 4);
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.fillRect(screenX + 16, this.y - 16, 4, 20);
    ctx.shadowColor = gold;
    ctx.shadowBlur = 8;
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
    ctx.lineWidth = 1;
    ctx.strokeRect(screenX + 6, this.y - 20, 38, 95);
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  drawOFTS(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 10;
    const camoBase = '#4A5568';
    const camoDark = '#2D3748';
    const camoLight = '#718096';
    const camoAccent = '#A0AEC0';
    const black = '#1a1a1a';
    const skinTone = '#FFCC99';
    const gold = '#FFD700';
    
    ctx.save();
    if (this.direction === -1) {
      ctx.scale(-1, 1);
      screenX = -screenX - this.width;
    }
    
    const drawCamo = (x, y, w, h) => {
      ctx.fillStyle = camoBase;
      ctx.fillRect(x, y, w, h);
      for (let i = 0; i < 8; i++) {
        const cx = x + Math.random() * (w - 4);
        const cy = y + Math.random() * (h - 4);
        ctx.fillStyle = [camoDark, camoLight, camoAccent][Math.floor(i % 3)];
        ctx.fillRect(cx, cy, 3 + Math.random() * 3, 3 + Math.random() * 3);
      }
    };
    
    drawCamo(screenX + 12, this.y + 50 + legOffset, 10, 18);
    drawCamo(screenX + 28, this.y + 50 - legOffset, 10, 18);
    ctx.fillStyle = black;
    ctx.fillRect(screenX + 10, this.y + 66 + legOffset, 14, 8);
    ctx.fillRect(screenX + 26, this.y + 66 - legOffset, 14, 8);
    ctx.fillStyle = '#333';
    ctx.fillRect(screenX + 9, this.y + 72 + legOffset, 16, 3);
    ctx.fillRect(screenX + 25, this.y + 72 - legOffset, 16, 3);
    drawCamo(screenX + 8, this.y + 28, 34, 26);
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.fillRect(screenX + 10, this.y + 30, 30, 22);
    ctx.fillStyle = '#333';
    ctx.fillRect(screenX + 14, this.y + 28, 4, 26);
    ctx.fillRect(screenX + 32, this.y + 28, 4, 26);
    ctx.fillStyle = black;
    ctx.beginPath();
    ctx.moveTo(screenX + 22, this.y + 30);
    ctx.lineTo(screenX + 25, this.y + 33);
    ctx.lineTo(screenX + 28, this.y + 30);
    ctx.lineTo(screenX + 25, this.y + 27);
    ctx.closePath();
    ctx.fill();
    
    ctx.save();
    ctx.translate(screenX + 8, this.y + 30);
    ctx.rotate(-armSwing * Math.PI / 180);
    drawCamo(-4, 0, 8, 20);
    ctx.fillStyle = camoBase;
    ctx.fillRect(-3, 18, 6, 6);
    ctx.restore();
    
    ctx.save();
    ctx.translate(screenX + 42, this.y + 30);
    const isThrowing = this.grenadeThrowAnim > 0;
    const throwAngle = isThrowing ? -45 + (this.grenadeThrowAnim / 30) * 60 : armSwing;
    ctx.rotate(throwAngle * Math.PI / 180);
    drawCamo(-4, 0, 8, 18);
    ctx.fillStyle = camoBase;
    ctx.fillRect(-3, 16, 6, 6);
    if (isThrowing) {
      ctx.fillStyle = '#4A5C3D';
      ctx.beginPath();
      ctx.arc(0, 20, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#888';
      ctx.fillRect(-2, 16, 4, 3);
    } else {
      ctx.fillStyle = '#333';
      ctx.fillRect(2, 12, 22, 7);
      ctx.fillStyle = '#222';
      ctx.fillRect(20, 13, 12, 5);
      ctx.fillStyle = '#FF4500';
      ctx.fillRect(30, 14, 4, 3);
      ctx.fillStyle = '#444';
      ctx.beginPath();
      ctx.arc(10, 20, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#FF6B00';
      ctx.font = '6px Arial';
      ctx.fillText('\u{1F525}', 6, 14);
    }
    ctx.restore();
    
    ctx.fillStyle = skinTone;
    ctx.fillRect(screenX + 12, this.y + 8, 26, 22);
    ctx.fillStyle = camoDark;
    ctx.fillRect(screenX + 14, this.y + 12, 8, 3);
    ctx.fillRect(screenX + 28, this.y + 14, 8, 3);
    ctx.fillRect(screenX + 18, this.y + 22, 10, 2);
    ctx.strokeStyle = gold;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(screenX + 32, this.y + 16, 5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(screenX + 37, this.y + 16);
    ctx.quadraticCurveTo(screenX + 42, this.y + 22, screenX + 38, this.y + 28);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = black;
    ctx.fillRect(screenX + 18, this.y + 14, 4, 5);
    ctx.fillRect(screenX + 30, this.y + 14, 4, 5);
    ctx.fillStyle = black;
    ctx.save();
    ctx.translate(screenX + 20, this.y + 12);
    ctx.rotate(-0.3);
    ctx.fillRect(-3, 0, 8, 2);
    ctx.restore();
    ctx.save();
    ctx.translate(screenX + 32, this.y + 12);
    ctx.rotate(0.3);
    ctx.fillRect(-3, 0, 8, 2);
    ctx.restore();
    ctx.strokeStyle = black;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(screenX + 20, this.y + 25);
    ctx.lineTo(screenX + 30, this.y + 25);
    ctx.stroke();
    ctx.fillStyle = black;
    ctx.fillRect(screenX + 8, this.y + 6, 34, 4);
    ctx.fillRect(screenX + 14, this.y - 18, 22, 26);
    ctx.fillStyle = '#8B0000';
    ctx.fillRect(screenX + 14, this.y + 2, 22, 4);
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.fillRect(screenX + 16, this.y - 16, 4, 20);
    ctx.shadowColor = '#FF4500';
    ctx.shadowBlur = 10;
    ctx.strokeStyle = 'rgba(255, 69, 0, 0.4)';
    ctx.lineWidth = 1;
    ctx.strokeRect(screenX + 6, this.y - 20, 38, 95);
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  // === CYBER PHANTOM: Neon wireframe warrior with circuit patterns ===
  drawCyberPhantom(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 12;
    const t = Date.now() / 1000;
    const pulse = 0.6 + Math.sin(t * 4) * 0.4;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    // Neon glow aura
    ctx.shadowColor = '#00FFFF'; ctx.shadowBlur = 12 * pulse;
    // Legs - dark with neon strips
    ctx.fillStyle = '#0a0a1a';
    ctx.fillRect(screenX + 12, this.y + 52 + legOffset, 10, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 10, 20);
    ctx.fillStyle = `rgba(0,255,255,${pulse})`;
    ctx.fillRect(screenX + 14, this.y + 54 + legOffset, 2, 16);
    ctx.fillRect(screenX + 30, this.y + 54 - legOffset, 2, 16);
    // Boots
    ctx.fillStyle = '#00FFFF';
    ctx.fillRect(screenX + 10, this.y + 70 + legOffset, 14, 4);
    ctx.fillRect(screenX + 26, this.y + 70 - legOffset, 14, 4);
    // Body - dark with circuit lines
    ctx.fillStyle = '#0a0a1a';
    ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    ctx.strokeStyle = `rgba(0,255,255,${pulse})`; ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(screenX + 12, this.y + 32); ctx.lineTo(screenX + 25, this.y + 32);
    ctx.lineTo(screenX + 25, this.y + 44); ctx.lineTo(screenX + 38, this.y + 44);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(screenX + 12, this.y + 50); ctx.lineTo(screenX + 38, this.y + 50);
    ctx.stroke();
    // Core glow
    ctx.fillStyle = `rgba(0,255,255,${0.4 + pulse * 0.3})`;
    ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 5, 0, Math.PI * 2); ctx.fill();
    // Arms
    ctx.fillStyle = '#0a0a1a';
    ctx.save(); ctx.translate(screenX + 6, this.y + 30); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#00FFFF'; ctx.fillRect(-2, 18, 4, 4); ctx.restore();
    ctx.save(); ctx.translate(screenX + 44, this.y + 30); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#0a0a1a'; ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#333'; ctx.fillRect(2, 16, 15, 5);
    ctx.fillStyle = '#00FFFF'; ctx.fillRect(14, 17, 5, 3); ctx.restore();
    // Head - visor
    ctx.fillStyle = '#0a0a1a'; ctx.fillRect(screenX + 12, this.y + 6, 26, 24);
    ctx.fillStyle = `rgba(0,255,255,${pulse})`; ctx.fillRect(screenX + 14, this.y + 14, 22, 8);
    // Scanning line
    const scanPos = Math.sin(t * 3) * 8;
    ctx.fillStyle = '#fff'; ctx.fillRect(screenX + 23 + scanPos, this.y + 15, 4, 6);
    // Antenna
    ctx.fillStyle = '#00FFFF'; ctx.fillRect(screenX + 23, this.y, 4, 8);
    ctx.beginPath(); ctx.arc(screenX + 25, this.y - 2, 3, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0; ctx.restore();
  }

  // === MOLTEN GUARDIAN: Lava warrior with ember glow ===
  drawMoltenGuardian(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 12;
    const t = Date.now() / 1000;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    ctx.shadowColor = '#FF4500'; ctx.shadowBlur = 8;
    // Legs - rocky
    ctx.fillStyle = '#4A1A00';
    ctx.fillRect(screenX + 12, this.y + 52 + legOffset, 10, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 10, 20);
    ctx.fillStyle = '#FF4500';
    ctx.fillRect(screenX + 14, this.y + 58 + legOffset, 6, 3);
    ctx.fillRect(screenX + 30, this.y + 60 - legOffset, 6, 3);
    // Boots - molten
    ctx.fillStyle = '#8B2500'; ctx.fillRect(screenX + 10, this.y + 70 + legOffset, 14, 5);
    ctx.fillRect(screenX + 26, this.y + 70 - legOffset, 14, 5);
    // Body - dark rock with lava cracks
    ctx.fillStyle = '#3D1400'; ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    ctx.strokeStyle = '#FF6B00'; ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(screenX + 15, this.y + 30); ctx.lineTo(screenX + 22, this.y + 42);
    ctx.lineTo(screenX + 18, this.y + 52); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(screenX + 35, this.y + 33); ctx.lineTo(screenX + 28, this.y + 45);
    ctx.lineTo(screenX + 33, this.y + 53); ctx.stroke();
    // Molten core
    ctx.fillStyle = `rgba(255,100,0,${0.5 + Math.sin(t * 3) * 0.3})`;
    ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 7, 0, Math.PI * 2); ctx.fill();
    // Arms
    ctx.fillStyle = '#4A1A00';
    ctx.save(); ctx.translate(screenX + 6, this.y + 30); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#FF4500'; ctx.fillRect(-2, 18, 6, 5); ctx.restore();
    ctx.save(); ctx.translate(screenX + 44, this.y + 30); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#4A1A00'; ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#333'; ctx.fillRect(2, 14, 15, 6);
    ctx.fillStyle = '#FF4500'; ctx.fillRect(14, 15, 5, 4); ctx.restore();
    // Head - horned helmet
    ctx.fillStyle = '#3D1400'; ctx.fillRect(screenX + 12, this.y + 6, 26, 24);
    ctx.fillStyle = '#FF4500';
    ctx.fillRect(screenX + 18, this.y + 14, 5, 5); ctx.fillRect(screenX + 28, this.y + 14, 5, 5);
    // Horns
    ctx.fillStyle = '#1a0a00';
    ctx.beginPath(); ctx.moveTo(screenX + 12, this.y + 10); ctx.lineTo(screenX + 8, this.y - 8);
    ctx.lineTo(screenX + 18, this.y + 6); ctx.fill();
    ctx.beginPath(); ctx.moveTo(screenX + 38, this.y + 10); ctx.lineTo(screenX + 42, this.y - 8);
    ctx.lineTo(screenX + 32, this.y + 6); ctx.fill();
    // Mouth glow
    ctx.fillStyle = '#FF6B00'; ctx.fillRect(screenX + 18, this.y + 24, 14, 3);
    ctx.shadowBlur = 0; ctx.restore();
  }

  // === ARCTIC SENTINEL: Ice crystal warrior ===
  drawArcticSentinel(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 12;
    const t = Date.now() / 1000;
    const shimmer = 0.7 + Math.sin(t * 2) * 0.3;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    ctx.shadowColor = '#87CEEB'; ctx.shadowBlur = 6;
    // Legs - ice blue
    ctx.fillStyle = '#5BA3C7';
    ctx.fillRect(screenX + 12, this.y + 52 + legOffset, 10, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 10, 20);
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.fillRect(screenX + 13, this.y + 54 + legOffset, 3, 8);
    ctx.fillRect(screenX + 29, this.y + 56 - legOffset, 3, 8);
    // Boots
    ctx.fillStyle = '#4A8EA8'; ctx.fillRect(screenX + 10, this.y + 70 + legOffset, 14, 5);
    ctx.fillRect(screenX + 26, this.y + 70 - legOffset, 14, 5);
    // Body - ice crystal armor
    ctx.fillStyle = '#6BB8D4'; ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    ctx.fillStyle = `rgba(255,255,255,${shimmer * 0.4})`;
    // Crystal facets
    ctx.beginPath(); ctx.moveTo(screenX + 15, this.y + 30); ctx.lineTo(screenX + 25, this.y + 38);
    ctx.lineTo(screenX + 15, this.y + 46); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.moveTo(screenX + 35, this.y + 32); ctx.lineTo(screenX + 25, this.y + 40);
    ctx.lineTo(screenX + 35, this.y + 48); ctx.closePath(); ctx.fill();
    // Frost core
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 4, 0, Math.PI * 2); ctx.fill();
    // Arms
    ctx.fillStyle = '#5BA3C7';
    ctx.save(); ctx.translate(screenX + 6, this.y + 30); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#fff'; ctx.fillRect(-2, 18, 6, 5); ctx.restore();
    ctx.save(); ctx.translate(screenX + 44, this.y + 30); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#5BA3C7'; ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#4A8EA8'; ctx.fillRect(2, 14, 15, 6);
    ctx.fillStyle = '#87CEEB'; ctx.fillRect(14, 15, 5, 4); ctx.restore();
    // Head
    ctx.fillStyle = '#6BB8D4'; ctx.fillRect(screenX + 12, this.y + 8, 26, 22);
    ctx.fillStyle = '#fff';
    ctx.fillRect(screenX + 18, this.y + 14, 4, 5); ctx.fillRect(screenX + 28, this.y + 14, 4, 5);
    // Ice crown
    ctx.fillStyle = `rgba(135,206,235,${shimmer})`;
    for (let i = 0; i < 5; i++) {
      const cx = screenX + 14 + i * 6;
      ctx.beginPath(); ctx.moveTo(cx, this.y + 8); ctx.lineTo(cx + 3, this.y - 4 - (i === 2 ? 6 : 0));
      ctx.lineTo(cx + 6, this.y + 8); ctx.fill();
    }
    ctx.shadowBlur = 0; ctx.restore();
  }

  // === SHADOW ASSASSIN: Stealth ninja with blade arms ===
  drawShadowAssassin(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 15;
    const t = Date.now() / 1000;
    const flicker = 0.8 + Math.sin(t * 6) * 0.2;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    // Subtle shadow aura
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 30, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = flicker;
    // Legs - slim dark
    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(screenX + 14, this.y + 52 + legOffset, 8, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 8, 20);
    // Tabi boots
    ctx.fillStyle = '#1a1a2e'; ctx.fillRect(screenX + 12, this.y + 70 + legOffset, 12, 4);
    ctx.fillRect(screenX + 26, this.y + 70 - legOffset, 12, 4);
    // Body - dark wrap
    ctx.fillStyle = '#0d0d1a'; ctx.fillRect(screenX + 10, this.y + 28, 30, 26);
    // Purple shimmer
    ctx.fillStyle = `rgba(153,69,255,${0.15 + Math.sin(t * 3) * 0.1})`;
    ctx.fillRect(screenX + 10, this.y + 28, 30, 26);
    // Belt with shurikens
    ctx.fillStyle = '#333'; ctx.fillRect(screenX + 10, this.y + 50, 30, 4);
    ctx.fillStyle = '#9945FF';
    ctx.beginPath(); ctx.arc(screenX + 20, this.y + 52, 2, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(screenX + 30, this.y + 52, 2, 0, Math.PI * 2); ctx.fill();
    // Arms with blades
    ctx.fillStyle = '#0d0d1a';
    ctx.save(); ctx.translate(screenX + 8, this.y + 30); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-3, 0, 7, 20);
    ctx.fillStyle = '#666'; ctx.fillRect(-2, 18, 5, 10); // Blade
    ctx.fillStyle = '#aaa'; ctx.fillRect(-1, 22, 3, 8); ctx.restore();
    ctx.save(); ctx.translate(screenX + 42, this.y + 30); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#0d0d1a'; ctx.fillRect(-3, 0, 7, 20);
    ctx.fillStyle = '#333'; ctx.fillRect(2, 14, 15, 5); // Gun
    ctx.fillStyle = '#9945FF'; ctx.fillRect(14, 15, 5, 3); ctx.restore();
    // Head - ninja hood
    ctx.fillStyle = '#0d0d1a'; ctx.fillRect(screenX + 12, this.y + 6, 26, 24);
    ctx.fillStyle = '#1a1a2e'; ctx.fillRect(screenX + 12, this.y + 6, 26, 10); // Hood top
    // Only eyes visible
    ctx.fillStyle = '#9945FF';
    ctx.fillRect(screenX + 18, this.y + 16, 4, 3); ctx.fillRect(screenX + 28, this.y + 16, 4, 3);
    ctx.globalAlpha = 1; ctx.restore();
  }

  // === NATURE SPIRIT: Forest guardian with bark and vines ===
  drawNatureSpirit(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 12;
    const t = Date.now() / 1000;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    // Legs - root-like
    ctx.fillStyle = '#5C3A1E';
    ctx.fillRect(screenX + 12, this.y + 52 + legOffset, 10, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 10, 20);
    // Root feet
    ctx.fillStyle = '#4A2E14'; ctx.fillRect(screenX + 9, this.y + 70 + legOffset, 16, 5);
    ctx.fillRect(screenX + 25, this.y + 70 - legOffset, 16, 5);
    // Vine wraps on legs
    ctx.strokeStyle = '#228B22'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(screenX + 13, this.y + 54 + legOffset);
    ctx.lineTo(screenX + 20, this.y + 60 + legOffset); ctx.lineTo(screenX + 14, this.y + 66 + legOffset); ctx.stroke();
    // Body - bark texture
    ctx.fillStyle = '#5C3A1E'; ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    // Bark lines
    ctx.strokeStyle = '#4A2E14'; ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath(); ctx.moveTo(screenX + 12 + i * 8, this.y + 30);
      ctx.lineTo(screenX + 10 + i * 8, this.y + 54); ctx.stroke();
    }
    // Green vine overlay
    ctx.strokeStyle = '#228B22'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(screenX + 10, this.y + 35);
    ctx.quadraticCurveTo(screenX + 25, this.y + 32, screenX + 38, this.y + 38); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(screenX + 12, this.y + 48);
    ctx.quadraticCurveTo(screenX + 30, this.y + 52, screenX + 40, this.y + 46); ctx.stroke();
    // Green heart
    ctx.fillStyle = '#14F195'; ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 4, 0, Math.PI * 2); ctx.fill();
    // Arms - branch-like
    ctx.fillStyle = '#5C3A1E';
    ctx.save(); ctx.translate(screenX + 6, this.y + 30); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 20);
    // Leaf hand
    ctx.fillStyle = '#228B22'; ctx.beginPath();
    ctx.ellipse(0, 22, 5, 4, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
    ctx.save(); ctx.translate(screenX + 44, this.y + 30); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#5C3A1E'; ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#4A2E14'; ctx.fillRect(2, 14, 15, 6); // Wooden gun
    ctx.fillStyle = '#228B22'; ctx.fillRect(14, 15, 5, 4); ctx.restore();
    // Head - wooden face
    ctx.fillStyle = '#6B4226'; ctx.fillRect(screenX + 12, this.y + 8, 26, 22);
    ctx.fillStyle = '#228B22'; // Eyes
    ctx.fillRect(screenX + 18, this.y + 14, 4, 5); ctx.fillRect(screenX + 28, this.y + 14, 4, 5);
    ctx.fillStyle = '#4A2E14'; ctx.fillRect(screenX + 20, this.y + 23, 10, 3); // Mouth
    // Leaf crown
    ctx.fillStyle = '#228B22';
    for (let i = 0; i < 4; i++) {
      const lx = screenX + 13 + i * 7;
      ctx.beginPath(); ctx.ellipse(lx, this.y + 6, 5, 3, -0.3 + i * 0.2, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  // === VOID WALKER: Dark energy ethereal being ===
  drawVoidWalker(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 12;
    const t = Date.now() / 1000;
    const pulse = 0.5 + Math.sin(t * 3) * 0.5;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    // Void aura
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = '#8B00FF';
    ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 35 + Math.sin(t * 2) * 5, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.shadowColor = '#8B00FF'; ctx.shadowBlur = 10;
    // Legs - energy wisps
    ctx.fillStyle = '#1a0033';
    ctx.fillRect(screenX + 12, this.y + 52 + legOffset, 10, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 10, 20);
    ctx.fillStyle = `rgba(139,0,255,${pulse * 0.5})`;
    ctx.fillRect(screenX + 14, this.y + 56 + legOffset, 6, 12);
    ctx.fillRect(screenX + 30, this.y + 56 - legOffset, 6, 12);
    // Energy feet
    ctx.fillStyle = '#8B00FF'; ctx.fillRect(screenX + 10, this.y + 70 + legOffset, 14, 4);
    ctx.fillRect(screenX + 26, this.y + 70 - legOffset, 14, 4);
    // Body - dark with energy veins
    ctx.fillStyle = '#0d0020'; ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    ctx.strokeStyle = `rgba(139,0,255,${pulse})`; ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + t;
      ctx.beginPath(); ctx.moveTo(screenX + 25, this.y + 40);
      ctx.lineTo(screenX + 25 + Math.cos(angle) * 14, this.y + 40 + Math.sin(angle) * 10); ctx.stroke();
    }
    // Void core
    ctx.fillStyle = '#8B00FF'; ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 5, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 2, 0, Math.PI * 2); ctx.fill();
    // Arms
    ctx.fillStyle = '#1a0033';
    ctx.save(); ctx.translate(screenX + 6, this.y + 30); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#8B00FF'; ctx.fillRect(-2, 18, 6, 5); ctx.restore();
    ctx.save(); ctx.translate(screenX + 44, this.y + 30); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#1a0033'; ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#333'; ctx.fillRect(2, 14, 15, 6);
    ctx.fillStyle = '#8B00FF'; ctx.fillRect(14, 15, 5, 4); ctx.restore();
    // Head - void orb
    ctx.fillStyle = '#0d0020'; ctx.fillRect(screenX + 12, this.y + 6, 26, 24);
    // Rune symbols
    ctx.fillStyle = `rgba(139,0,255,${pulse})`;
    ctx.fillRect(screenX + 16, this.y + 10, 3, 3); ctx.fillRect(screenX + 31, this.y + 10, 3, 3);
    // Glowing eyes
    ctx.fillStyle = '#8B00FF';
    ctx.beginPath(); ctx.arc(screenX + 20, this.y + 17, 3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(screenX + 30, this.y + 17, 3, 0, Math.PI * 2); ctx.fill();
    // Third eye
    ctx.fillStyle = '#fff';
    ctx.beginPath(); ctx.arc(screenX + 25, this.y + 8, 2, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0; ctx.restore();
  }

  // === SOLAR KNIGHT: Golden radiant warrior ===
  drawSolarKnight(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 12;
    const t = Date.now() / 1000;
    const glow = 0.6 + Math.sin(t * 2) * 0.4;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    ctx.shadowColor = '#FFD700'; ctx.shadowBlur = 8 * glow;
    // Light halo
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = '#FFD700';
    ctx.beginPath(); ctx.arc(screenX + 25, this.y + 30, 35, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    // Legs - gold armor
    ctx.fillStyle = '#B8860B';
    ctx.fillRect(screenX + 12, this.y + 52 + legOffset, 10, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 10, 20);
    ctx.fillStyle = '#FFD700'; // Knee guards
    ctx.fillRect(screenX + 11, this.y + 56 + legOffset, 12, 4);
    ctx.fillRect(screenX + 27, this.y + 56 - legOffset, 12, 4);
    // Boots
    ctx.fillStyle = '#B8860B'; ctx.fillRect(screenX + 10, this.y + 70 + legOffset, 14, 5);
    ctx.fillRect(screenX + 26, this.y + 70 - legOffset, 14, 5);
    // Body - golden armor plates
    ctx.fillStyle = '#DAA520'; ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    // Chest plate highlight
    ctx.fillStyle = '#FFD700'; ctx.fillRect(screenX + 12, this.y + 30, 26, 20);
    // Sun emblem
    ctx.fillStyle = '#FF8C00';
    ctx.beginPath(); ctx.arc(screenX + 25, this.y + 40, 6, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = '#FFD700';
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2 + t * 0.5;
      ctx.fillRect(screenX + 24 + Math.cos(angle) * 8, this.y + 39 + Math.sin(angle) * 8, 3, 3);
    }
    // Shoulder pauldrons
    ctx.fillStyle = '#DAA520';
    ctx.fillRect(screenX + 2, this.y + 26, 12, 8); ctx.fillRect(screenX + 36, this.y + 26, 12, 8);
    // Arms
    ctx.fillStyle = '#B8860B';
    ctx.save(); ctx.translate(screenX + 6, this.y + 32); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 18);
    ctx.fillStyle = '#FFD700'; ctx.fillRect(-3, 16, 7, 6); ctx.restore();
    ctx.save(); ctx.translate(screenX + 44, this.y + 32); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#B8860B'; ctx.fillRect(-4, 0, 8, 18);
    ctx.fillStyle = '#DAA520'; ctx.fillRect(2, 14, 15, 6); // Gold gun
    ctx.fillStyle = '#FFD700'; ctx.fillRect(14, 15, 5, 4); ctx.restore();
    // Head - knight helmet
    ctx.fillStyle = '#DAA520'; ctx.fillRect(screenX + 12, this.y + 6, 26, 24);
    ctx.fillStyle = '#B8860B'; // Visor
    ctx.fillRect(screenX + 14, this.y + 16, 22, 6);
    ctx.fillStyle = '#FFD700'; // Eyes through visor
    ctx.fillRect(screenX + 17, this.y + 17, 4, 4); ctx.fillRect(screenX + 29, this.y + 17, 4, 4);
    // Sun crest on helmet
    ctx.fillStyle = '#FF8C00';
    ctx.beginPath(); ctx.moveTo(screenX + 20, this.y + 8); ctx.lineTo(screenX + 25, this.y - 4);
    ctx.lineTo(screenX + 30, this.y + 8); ctx.fill();
    ctx.fillStyle = '#FFD700'; ctx.beginPath(); ctx.arc(screenX + 25, this.y + 4, 3, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0; ctx.restore();
  }

  // === THUNDER LORD: Storm-charged lightning warrior ===
  drawThunderLord(ctx, screenX) {
    const legOffset = Math.sin(this.animFrame * Math.PI) * 5;
    const armSwing = Math.sin(this.animFrame * Math.PI) * 12;
    const t = Date.now() / 1000;
    const spark = Math.random() > 0.7 ? 1 : 0.5;
    ctx.save();
    if (this.direction === -1) { ctx.scale(-1, 1); screenX = -screenX - this.width; }
    ctx.shadowColor = '#FFFF00'; ctx.shadowBlur = 6 * spark;
    // Legs - dark blue with lightning
    ctx.fillStyle = '#1a1a4a';
    ctx.fillRect(screenX + 12, this.y + 52 + legOffset, 10, 20);
    ctx.fillRect(screenX + 28, this.y + 52 - legOffset, 10, 20);
    // Lightning bolts on legs
    ctx.strokeStyle = '#FFFF00'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(screenX + 16, this.y + 54 + legOffset);
    ctx.lineTo(screenX + 14, this.y + 62 + legOffset); ctx.lineTo(screenX + 18, this.y + 60 + legOffset);
    ctx.lineTo(screenX + 15, this.y + 68 + legOffset); ctx.stroke();
    // Boots
    ctx.fillStyle = '#2a2a5a'; ctx.fillRect(screenX + 10, this.y + 70 + legOffset, 14, 5);
    ctx.fillRect(screenX + 26, this.y + 70 - legOffset, 14, 5);
    // Body - dark blue storm armor
    ctx.fillStyle = '#1a1a4a'; ctx.fillRect(screenX + 8, this.y + 28, 34, 26);
    // Lightning bolt emblem
    ctx.fillStyle = '#FFFF00';
    ctx.beginPath();
    ctx.moveTo(screenX + 22, this.y + 30); ctx.lineTo(screenX + 18, this.y + 40);
    ctx.lineTo(screenX + 24, this.y + 38); ctx.lineTo(screenX + 20, this.y + 52);
    ctx.lineTo(screenX + 28, this.y + 40); ctx.lineTo(screenX + 22, this.y + 42);
    ctx.lineTo(screenX + 28, this.y + 30); ctx.fill();
    // Electric sparks (random)
    if (spark > 0.7) {
      ctx.strokeStyle = '#FFFF00'; ctx.lineWidth = 1;
      for (let i = 0; i < 3; i++) {
        const sx = screenX + 10 + Math.random() * 30;
        const sy = this.y + 28 + Math.random() * 26;
        ctx.beginPath(); ctx.moveTo(sx, sy);
        ctx.lineTo(sx + (Math.random() - 0.5) * 8, sy + (Math.random() - 0.5) * 8); ctx.stroke();
      }
    }
    // Arms with electric gauntlets
    ctx.fillStyle = '#1a1a4a';
    ctx.save(); ctx.translate(screenX + 6, this.y + 30); ctx.rotate(-armSwing * Math.PI / 180);
    ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#FFFF00'; ctx.fillRect(-3, 17, 7, 6); ctx.restore();
    ctx.save(); ctx.translate(screenX + 44, this.y + 30); ctx.rotate(armSwing * Math.PI / 180);
    ctx.fillStyle = '#1a1a4a'; ctx.fillRect(-4, 0, 8, 20);
    ctx.fillStyle = '#333'; ctx.fillRect(2, 14, 15, 6);
    ctx.fillStyle = '#FFFF00'; ctx.fillRect(14, 15, 5, 4); ctx.restore();
    // Head - storm crown
    ctx.fillStyle = '#1a1a4a'; ctx.fillRect(screenX + 12, this.y + 6, 26, 24);
    // Electric eyes
    ctx.fillStyle = '#FFFF00';
    ctx.fillRect(screenX + 18, this.y + 14, 4, 5); ctx.fillRect(screenX + 28, this.y + 14, 4, 5);
    // Storm crown - lightning spikes
    ctx.fillStyle = '#FFFF00';
    ctx.beginPath(); ctx.moveTo(screenX + 12, this.y + 8); ctx.lineTo(screenX + 15, this.y - 6);
    ctx.lineTo(screenX + 18, this.y + 8); ctx.fill();
    ctx.beginPath(); ctx.moveTo(screenX + 20, this.y + 8); ctx.lineTo(screenX + 25, this.y - 10);
    ctx.lineTo(screenX + 30, this.y + 8); ctx.fill();
    ctx.beginPath(); ctx.moveTo(screenX + 32, this.y + 8); ctx.lineTo(screenX + 35, this.y - 6);
    ctx.lineTo(screenX + 38, this.y + 8); ctx.fill();
    // Determined mouth
    ctx.fillStyle = '#FFFF00'; ctx.fillRect(screenX + 20, this.y + 24, 10, 2);
    ctx.shadowBlur = 0; ctx.restore();
  }

  drawTankMode(ctx, screenX) {
    const tankY = this.y + 15;
    ctx.fillStyle = '#4A90D9';
    ctx.fillRect(screenX - 10, tankY + 10, 60, 30);
    ctx.fillRect(screenX + 5, tankY, 30, 15);
    ctx.fillStyle = '#333';
    const cannonLength = 25 * this.direction;
    ctx.fillRect(this.direction === 1 ? screenX + 35 : screenX - 20, tankY + 3, Math.abs(cannonLength), 8);
    ctx.fillStyle = '#14F195';
    ctx.fillRect(screenX + 10, tankY + 3, 20, 8);
    ctx.fillStyle = '#222';
    ctx.fillRect(screenX - 15, tankY + 38, 70, 10);
    for (let i = 0; i < 7; i++) {
      ctx.fillStyle = '#444';
      ctx.fillRect(screenX - 10 + i * 10 + (this.animFrame * 3) % 10, tankY + 40, 6, 6);
    }
    ctx.shadowColor = '#4A90D9';
    ctx.shadowBlur = 10;
    ctx.strokeStyle = '#4A90D9';
    ctx.lineWidth = 2;
    ctx.strokeRect(screenX - 12, tankY + 8, 64, 42);
    ctx.shadowBlur = 0;
  }

  darkenColor(hex, amount) {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, (num >> 16) - amount);
    const g = Math.max(0, ((num >> 8) & 0x00FF) - amount);
    const b = Math.max(0, (num & 0x0000FF) - amount);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  }
}

export default Player;
